import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useVisibilityPolling } from "@/hooks/useVisibilityPolling";

export type NotificationType =
  | "portal_response"
  | "ticket_reply"
  | "contract_signed"
  | "team_invite_accepted";

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  href?: string;
  createdAt: string;
}

const READ_STORAGE_KEY = "sf_notifications_read_v1";
const LAST_SEEN_KEY = "sf_notifications_last_seen_v1";

const getReadIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(READ_STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
};

const saveReadIds = (ids: Set<string>) => {
  try {
    localStorage.setItem(READ_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    /* ignore */
  }
};

/**
 * Aggregates notifications from four sources:
 *  - client_portal_shares responses
 *  - ticket_messages on user's tickets (from staff)
 *  - contracts that have been signed
 *  - team_members who accepted an invite
 * Read state is per-browser via localStorage.
 */
export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(getReadIds());
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    // 14-day lookback (UI only shows the latest 30 anyway) — cuts query bytes.
    const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
    const list: AppNotification[] = [];

    // 1. Portal responses
    const { data: shares } = await supabase
      .from("client_portal_shares")
      .select("id, status, client_name, client_email, responded_at, proposal_id")
      .eq("shared_by", user.id)
      .in("status", ["approved", "rejected"])
      .not("responded_at", "is", null)
      .gte("responded_at", since)
      .order("responded_at", { ascending: false })
      .limit(20);

    shares?.forEach((s: any) => {
      list.push({
        id: `portal-${s.id}`,
        type: "portal_response",
        title: `Client ${s.status === "approved" ? "approved" : "rejected"} proposal`,
        description: `${s.client_name || s.client_email}`,
        href: "/history",
        createdAt: s.responded_at,
      });
    });

    // 2. Ticket replies (staff messages on user's tickets)
    const { data: tickets } = await supabase
      .from("support_tickets")
      .select("id, subject")
      .eq("user_id", user.id);
    const ticketIds = (tickets ?? []).map((t: any) => t.id);
    if (ticketIds.length > 0) {
      const { data: msgs } = await supabase
        .from("ticket_messages")
        .select("id, ticket_id, sender_id, sender_type, created_at, message, is_internal_note")
        .in("ticket_id", ticketIds)
        .neq("sender_id", user.id)
        .eq("is_internal_note", false)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(20);
      msgs?.forEach((m: any) => {
        const t = tickets?.find((x: any) => x.id === m.ticket_id);
        list.push({
          id: `tmsg-${m.id}`,
          type: "ticket_reply",
          title: "New support reply",
          description: t?.subject || "View your ticket",
          href: "/support",
          createdAt: m.created_at,
        });
      });
    }

    // 3. Contracts signed
    const { data: contracts } = await supabase
      .from("contracts")
      .select("id, title, client_name, signed_at, status")
      .eq("user_id", user.id)
      .eq("status", "signed")
      .not("signed_at", "is", null)
      .gte("signed_at", since)
      .order("signed_at", { ascending: false })
      .limit(20);
    contracts?.forEach((c: any) => {
      list.push({
        id: `contract-${c.id}`,
        type: "contract_signed",
        title: "Contract signed",
        description: `${c.title} — ${c.client_name}`,
        href: "/contracts",
        createdAt: c.signed_at,
      });
    });

    // 4. Team invites accepted
    const { data: ownedTeams } = await supabase
      .from("teams")
      .select("id")
      .eq("owner_id", user.id);
    const teamIds = (ownedTeams ?? []).map((t: any) => t.id);
    if (teamIds.length > 0) {
      const { data: members } = await supabase
        .from("team_members")
        .select("id, email, joined_at, status")
        .in("team_id", teamIds)
        .eq("status", "active")
        .not("joined_at", "is", null)
        .gte("joined_at", since)
        .order("joined_at", { ascending: false })
        .limit(20);
      members?.forEach((m: any) => {
        list.push({
          id: `tinv-${m.id}`,
          type: "team_invite_accepted",
          title: "Team invite accepted",
          description: m.email,
          href: "/team",
          createdAt: m.joined_at,
        });
      });
    }

    list.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    setNotifications(list.slice(0, 30));
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Poll every 3 hours while the tab is visible. Hidden tabs are skipped.
  // Bumped from 15 min → 3h to slash Cloud usage; users can still pull-to-refresh.
  useVisibilityPolling(fetchAll, 3 * 60 * 60_000, !!user);

  const unreadCount = notifications.filter((n) => !readIds.has(n.id)).length;

  const markAllRead = useCallback(() => {
    const next = new Set(readIds);
    notifications.forEach((n) => next.add(n.id));
    setReadIds(next);
    saveReadIds(next);
    try {
      localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
    } catch {
      /* ignore */
    }
  }, [notifications, readIds]);

  const markRead = useCallback(
    (id: string) => {
      const next = new Set(readIds);
      next.add(id);
      setReadIds(next);
      saveReadIds(next);
    },
    [readIds]
  );

  return {
    notifications,
    loading,
    unreadCount,
    readIds,
    markAllRead,
    markRead,
    refresh: fetchAll,
  };
}
